import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { AspectRatio } from '../components/ui/aspect-ratio';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { motion } from 'framer-motion';

export default function GalleryPage() {
    const [activeTab, setActiveTab] = useState('All');

    const galleryImages = {
        All: [
            { src: 'https://images.pexels.com/photos/1571459/pexels-photo-1571459.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Modern living room' },
            { src: 'https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Contemporary kitchen' },
            { src: 'https://images.pexels.com/photos/1454806/pexels-photo-1454806.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Minimalist bedroom' },
            { src: 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Modern office space' },
            { src: 'https://images.pexels.com/photos/2089696/pexels-photo-2089696.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Elegant living area' },
            { src: 'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Stylish kitchen' },
            { src: 'https://images.pexels.com/photos/2062426/pexels-photo-2062426.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Cozy bedroom' },
            { src: 'https://images.pexels.com/photos/3773575/pexels-photo-3773575.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Professional workspace' },
            { src: 'https://images.pexels.com/photos/3773583/pexels-photo-3773583.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Contemporary living' }
        ],
        Living: [
            { src: 'https://images.pexels.com/photos/1571459/pexels-photo-1571459.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Modern living room' },
            { src: 'https://images.pexels.com/photos/2089696/pexels-photo-2089696.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Elegant living area' },
            { src: 'https://images.pexels.com/photos/3773583/pexels-photo-3773583.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Contemporary living' }
        ],
        Kitchen: [
            { src: 'https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Contemporary kitchen' },
            { src: 'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Stylish kitchen' }
        ],
        Bedroom: [
            { src: 'https://images.pexels.com/photos/1454806/pexels-photo-1454806.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Minimalist bedroom' },
            { src: 'https://images.pexels.com/photos/2062426/pexels-photo-2062426.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Cozy bedroom' }
        ],
        Office: [
            { src: 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Modern office space' },
            { src: 'https://images.pexels.com/photos/3773575/pexels-photo-3773575.jpeg?auto=compress&cs=tinysrgb&w=800', alt: 'Professional workspace' }
        ]
    };

    return (
        <div className="py-24 bg-gradient-to-br from-cream-50 via-white to-brand-light/20 pt-32" data-testid="gallery-page">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
                <div className="text-center mb-16">
                    <span className="text-brand-primary font-bold text-sm uppercase tracking-widest mb-4 block">
                        Portfolio
                    </span>
                    <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                        Our <span className="text-brand-primary">Work</span>
                    </h1>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                        Browse through our collection of residential and commercial interior design projects. Each space tells a unique story of transformation and elegance.
                    </p>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="gallery-tabs">
                    <TabsList className="mb-12 flex justify-center flex-wrap bg-white/50 backdrop-blur-sm p-1 rounded-full border border-gray-100 shadow-sm max-w-2xl mx-auto h-auto">
                        {Object.keys(galleryImages).map((category) => (
                            <TabsTrigger
                                key={category}
                                value={category}
                                className="rounded-full px-8 py-3 data-[state=active]:bg-brand-primary data-[state=active]:text-white transition-all duration-300"
                                data-testid={`gallery-tab-${category.toLowerCase()}`}
                            >
                                {category}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {Object.entries(galleryImages).map(([category, images]) => (
                        <TabsContent key={category} value={category} className="mt-0">
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                                {images.map((image, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.05 }}
                                    >
                                        <Card className="overflow-hidden group rounded-[2rem] border-none shadow-lg hover:shadow-2xl transition-all duration-500" data-testid="gallery-item-card">
                                            <AspectRatio ratio={4 / 3}>
                                                <Zoom>
                                                    <img
                                                        src={image.src}
                                                        alt={image.alt}
                                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 pointer-events-auto"
                                                    />
                                                </Zoom>
                                            </AspectRatio>
                                        </Card>
                                    </motion.div>
                                ))}
                            </div>
                        </TabsContent>
                    ))}
                </Tabs>
            </div>
        </div>
    );
}
